import { useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import {
  Flex,
  HStack,
  PrimaryButton,
  SecondaryButton,
  type SegmentedControlOption,
  Spacing,
  Text,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { DsSelect } from '~/components/common/DsSelect'
import {
  DetailIconButton,
  EditIconButton,
  MasterDataTable,
} from '~/components/common/MasterDataTable'
import { ToggleButton } from '~/components/common/ToggleButton'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { useCsvImportLink } from '~/hooks/useCsvImportReturn'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { downloadCsv } from '~/libs/downloadCsv'
import { convertAreaLabel } from '~/models/area'
import { todayCivilDate } from '~/models/civilDate'
import {
  buildCustomerContractCsvExport,
  toCustomerContractCsvExportRows,
} from '~/models/customerContractCsvExport'
import { commonUploadCsvFileName } from '~/pages/AdminUploads/models/downloadFileName'
import { AreaButtons } from '~/pages/CustomerInfo/components/AreaButtons'
import { ContractMonthlyTable } from '~/pages/CustomerInfo/components/ContractMonthlyTable'
import { ContractTrendControls } from '~/pages/CustomerInfo/components/ContractTrendControls'
import { CreateContractModal } from '~/pages/CustomerInfo/components/CreateContractModal'
import { EditContractModal } from '~/pages/CustomerInfo/components/EditContractModal'
import { buildContractColumns } from '~/pages/CustomerInfo/components/contractColumns'
import { useContractTrend } from '~/pages/CustomerInfo/hooks/useContractTrend'
import { useListSupplyPoints } from '~/pages/CustomerInfo/hooks/useCustomerInfoQueries'
import { useExportContractCsv } from '~/pages/CustomerInfo/hooks/useExportContractCsv'
import { VOLTAGES } from '~/pages/CustomerInfo/models/contractTrend'
import {
  STAGE_FILTERS,
  STAGE_FILTER_I18N_KEY,
  type StageFilter,
  VOLTAGE_I18N_KEY,
  compareSupplyPointsForList,
  matchesStageFilter,
} from '~/pages/CustomerInfo/models/display'
import type { SupplyPoint } from '~/pages/CustomerInfo/models/types'
import { routePaths } from '~/routes/routePaths'

/** 開閉の保存キー (CollapsibleFilterToolbar が接頭辞を足す)。 */
const FILTER_STORAGE_KEY = 'customerInfo.contract'

/** 契約推移タブ (AC-CIN-027 / 033)。 エリアは「すべて」+ 10 エリア (モックどおり)。 */
export const ContractTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const [areaCode, setAreaCode] = useState<AreaCode | undefined>(undefined)
  const [stageFilter, setStageFilter] = useState<StageFilter>('all')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  // 編集モーダルには「開いた時点のレコード」(楽観ロックの token を含む) を固定して渡す。
  // 一覧の背景再取得 (復帰・フォーカス) で差し替えると入力中の内容が消えるため、閉じるまで置き換えない。
  // 古い token での保存は server が CONFLICT で拒否し、モーダルの競合ダイアログ → 再読み込み (閉じて取り直し) へ進む。
  const [selected, setSelected] = useState<SupplyPoint | undefined>()

  const { data, isLoading, isError, refetch } = useListSupplyPoints(areaCode)
  // 推移の日別集計は backend (GetContractTrend) に条件 (エリア・年度範囲) を渡して取る
  const trendState = useContractTrend(areaCode)
  // 取り込んだらこのタブへ戻る (returnTo = 今の path + query。 別タブではなく同じタブで開く)
  const csvImportLink = useCsvImportLink(
    routePaths.adminUploadsCustomerContract.path,
  )
  const today = todayCivilDate()
  const exportCsv = useExportContractCsv()
  const showSuccessToast = useSuccessToast()
  const [csvError, setCsvError] = useState(false)

  /**
   * CSVエクスポート: 削除されていない全地点 (見込み・失注を含む) を 1 地点 1 行で出す。画面の絞り込みは
   * 反映しない (ダウンロードしたものをそのまま取り込むと全件が据え置きになる)。ファイル名は
   * データ入稿画面のダウンロードと同じ命名 (`顧客契約_YYYYMMDD.csv`)。
   */
  const handleExportCsv = (): void => {
    setCsvError(false)
    exportCsv
      .mutateAsync({})
      .then((resp) => {
        downloadCsv(
          commonUploadCsvFileName(
            t('page:adminUploads.types.customerContract.name'),
          ),
          buildCustomerContractCsvExport(
            toCustomerContractCsvExportRows(resp.rows),
          ),
        )
        showSuccessToast({
          title: t('page:customerInfo.csv.downloaded', {
            count: resp.rows.length,
          }),
        })
      })
      .catch(() => setCsvError(true))
  }

  const rows = useMemo(
    () =>
      (data ?? [])
        .filter(
          (p) =>
            matchesStageFilter(p, stageFilter) &&
            trendState.voltages.has(p.voltageClass),
        )
        .sort(compareSupplyPointsForList),
    [data, stageFilter, trendState.voltages],
  )

  const stageOptions: Array<SegmentedControlOption<StageFilter>> =
    STAGE_FILTERS.map((s) => ({
      value: s,
      label: t(STAGE_FILTER_I18N_KEY[s]),
    }))

  const fyOptions = trendState.fiscalYears.map((fy) => ({
    value: String(fy),
    label: t('page:customerInfo.trend.fiscalYear', { year: fy }),
  }))

  // 開閉式のフィルター (React mock `CustomerList/tabs/ContractTrendTab.tsx` と同じ決め)。
  // 数える・戻すのはエリア・電圧階級・区分。年度は期間なので数えず、クリアでも戻さない
  const all = t('common:filter.all')
  const areaLabel = t('page:customerInfo.common.area')
  const fiscalYearLabel = t('page:customerInfo.trend.fiscalYearRange')
  const voltageLabel = t('page:customerInfo.list.filter.voltage')
  const stageLabel = t('page:customerInfo.stageFilter.label')
  const shownVoltages = VOLTAGES.filter((v) => trendState.voltages.has(v))
  const isAllVoltages = shownVoltages.length === VOLTAGES.length
  const modifiedCount = [
    areaCode !== undefined,
    !isAllVoltages,
    stageFilter !== 'all',
  ].filter(Boolean).length
  const clearFilters = (): void => {
    setAreaCode(undefined)
    trendState.resetVoltages()
    setStageFilter('all')
  }
  const fyText = (fy: number): string =>
    t('page:customerInfo.trend.fiscalYear', { year: fy })
  // 要約の並びは エリア → 年度 → 電圧階級 → 区分。電圧階級は全部なら「すべて」、それ以外は「・」でつなぐ
  const filterSummary: ToolbarStatusItem[] = [
    {
      key: 'area',
      label: areaLabel,
      value: areaCode === undefined ? all : convertAreaLabel(areaCode),
    },
    {
      key: 'fiscalYear',
      label: fiscalYearLabel,
      value:
        trendState.fyFrom === trendState.fyTo
          ? fyText(trendState.fyFrom)
          : `${fyText(trendState.fyFrom)}〜${fyText(trendState.fyTo)}`,
    },
    {
      key: 'voltage',
      label: voltageLabel,
      value: isAllVoltages
        ? all
        : shownVoltages.map((v) => t(VOLTAGE_I18N_KEY[v])).join('・') ||
          t('page:customerInfo.common.none'),
    },
    {
      key: 'stage',
      label: stageLabel,
      value:
        stageFilter === 'all' ? all : t(STAGE_FILTER_I18N_KEY[stageFilter]),
    },
  ]

  const resolveMemberName = useMemberDisplayName()
  const columns = buildContractColumns(t, today, resolveMemberName)

  return (
    <Flex flexDirection="column" gap={Spacing[3].value}>
      {/* CSV の導線 (モックの契約推移タブと同じ、フィルタの上の右寄せ前の行)。
          取込は差分プレビューつきの個別アップロード画面 (データ入稿) を同じタブで開く (AC-CIN-033)。 */}
      <HStack gap={2}>
        <SecondaryButton
          disabled={exportCsv.isPending}
          onClick={handleExportCsv}
          size="sm"
        >
          {t('page:customerInfo.csv.export')}
        </SecondaryButton>
        <SecondaryButton asChild size="sm">
          <Link to={csvImportLink}>{t('page:customerInfo.csv.import')}</Link>
        </SecondaryButton>
        {csvError && (
          <Text color="Text.negativeHigh" fontSize="sm">
            {t('page:customerInfo.csv.exportFailed')}
          </Text>
        )}
      </HStack>
      <CollapsibleFilterToolbar
        modifiedCount={modifiedCount}
        onClear={clearFilters}
        storageKey={FILTER_STORAGE_KEY}
        summary={filterSummary}
      >
        <FilterGrid
          items={[
            {
              key: 'area',
              label: areaLabel,
              control: (
                <AreaButtons
                  name="customer-info-contract-area"
                  onChange={setAreaCode}
                  value={areaCode}
                />
              ),
            },
            {
              key: 'fiscalYear',
              label: fiscalYearLabel,
              control: (
                <Flex alignItems="center" gap={2}>
                  <DsSelect
                    aria-label={t('page:customerInfo.trend.fiscalYearFrom')}
                    inputId="customer-info-trend-fy-from"
                    onChange={(v) =>
                      v !== undefined && trendState.setFyFrom(Number(v))
                    }
                    options={fyOptions.filter(
                      (o) => Number(o.value) <= trendState.fyTo,
                    )}
                    value={String(trendState.fyFrom)}
                  />
                  <Text as="span">〜</Text>
                  <DsSelect
                    aria-label={t('page:customerInfo.trend.fiscalYearTo')}
                    inputId="customer-info-trend-fy-to"
                    onChange={(v) =>
                      v !== undefined && trendState.setFyTo(Number(v))
                    }
                    options={fyOptions.filter(
                      (o) => Number(o.value) >= trendState.fyFrom,
                    )}
                    value={String(trendState.fyTo)}
                  />
                </Flex>
              ),
            },
            {
              // 電圧階級の絞り込みはこの 1 つ (モックの契約推移タブと同じ)。 一覧・グラフ・KPI・月次表が同じ選択に従う
              key: 'voltage',
              label: voltageLabel,
              control: (
                <Flex gap={1}>
                  {VOLTAGES.map((v) => (
                    <ToggleButton
                      key={v}
                      onClick={() => trendState.toggleVoltage(v)}
                      pressed={trendState.voltages.has(v)}
                      size="sm"
                    >
                      {t(VOLTAGE_I18N_KEY[v])}
                    </ToggleButton>
                  ))}
                </Flex>
              ),
            },
            {
              key: 'stage',
              label: stageLabel,
              control: (
                <AccessibleSegmentedControl
                  aria-label={stageLabel}
                  name="customer-info-stage-filter"
                  onSelect={setStageFilter}
                  options={stageOptions}
                  selected={stageFilter}
                />
              ),
            },
          ]}
        />
      </CollapsibleFilterToolbar>

      <ContractTrendControls state={trendState} />

      <Flex alignItems="center" justifyContent="space-between">
        <Text textStyle="sectionTitle">
          {t('page:customerInfo.contract.heading', { count: rows.length })}
        </Text>
        <Flex alignItems="center" gap={2}>
          {isError && (
            <SecondaryButton onClick={() => void refetch()} size="sm">
              {t('page:customerInfo.common.retry')}
            </SecondaryButton>
          )}
          <PrimaryButton
            onClick={() => setIsCreateOpen(true)}
            size="sm"
            title={t('page:customerInfo.modal.createContractTitle')}
          >
            {t('page:customerInfo.action.add')}
          </PrimaryButton>
        </Flex>
      </Flex>

      <MasterDataTable
        columns={columns}
        emptyLabel={t('page:customerInfo.contract.empty')}
        errorLabel={
          isError ? t('page:customerInfo.common.loadError') : undefined
        }
        getRowKey={(row) => row.id}
        isLoading={isLoading}
        onRowClick={(row) => setSelected(row)}
        renderRowActions={(row) =>
          row.stage === 'lost' ? (
            <DetailIconButton
              ariaLabel={t('page:customerInfo.contract.view')}
              onClick={() => setSelected(row)}
            />
          ) : (
            <EditIconButton onClick={() => setSelected(row)} />
          )
        }
        rows={rows}
      />

      <ContractMonthlyTable
        areaLabel={
          areaCode === undefined
            ? t('page:customerInfo.common.all')
            : convertAreaLabel(areaCode)
        }
        state={trendState}
      />

      {isCreateOpen && (
        <CreateContractModal
          defaultAreaCode={areaCode ?? AreaCode.TOKYO}
          mode="contract"
          onClose={() => setIsCreateOpen(false)}
        />
      )}
      {selected !== undefined && (
        <EditContractModal
          onClose={() => setSelected(undefined)}
          point={selected}
        />
      )}
    </Flex>
  )
}
